// Der Postausgang am Gerät (Plan E2, „Ich entscheide“: „Der Postausgang
// merkt sich, was sich geändert hat, nicht eine Kopie“).
//
//  - Je Sache (Art und Kennung) höchstens ein Eintrag. Ändert sich die
//    Sache wieder, bevor sie gesendet ist, wird nur `version` erhöht – die
//    Nummer, also der Platz in der Reihe, bleibt. So überholt nichts seine
//    Eltern: Die Aufnahme steht immer vor ihren Stationen, Längen, Notizen.
//  - Was gesendet wird, entsteht erst beim Senden aus der Arbeitskopie (je
//    Art eine Funktion unten) – immer der neueste Stand.
//  - Nach dem „ok“ des Servers wird der Eintrag nur gelöscht, wenn sich die
//    Sache seither nicht geändert hat; sonst geht der neue Stand noch hinaus.
//    Ohne diese Regel ginge eine Änderung verloren, die während des Sendens
//    getippt wurde.
//
// Seit E2, Schritt 2 stehen hier auch Dateien (Art „datei“, Kennung = die
// Prüfsumme): Ein Foto merkt erst seine Datei vor, dann sich selbst – so
// steht die Datei in der Reihe immer vor dem Foto, das sie braucht. Seit
// Schritt 3 ebenso das Sprachmemo (Notiz, Datei, Memo) und das K2-PDF
// (Datei, K2). Gesendet wird die Datei über den Datei-Eingang (PUT), nicht
// als Eintrag.
//
// Seit Schritt 4 der Abschluss („An das Büro senden“): Er steht immer
// hinten in der Reihe und trägt die Kennungen von allem, was am Server sein
// muss (`kennungenSammeln`). Fehlt dort etwas oder ist etwas zu viel, schickt
// die App es nach und stellt den Abschluss danach neu hinten an
// (`abschlussHeilen`, `hintenAnstellen`).
//
// Seit Schritt 5 „Etwas nachreichen“: Der Eintrag „nachtrag“ öffnet den
// Nachtrag am Server, alles, was darin entsteht, trägt seine Kennung
// (`nachtragId` am Eintrag im Postausgang – `vormerken` schreibt sie auch an
// einen, der schon wartet), und „nachtrag-abgeschlossen“ steht wie der erste
// Abschluss hinten, mit den Kennungen genau dieses Nachtrags
// (`nachtragStuecke` – dieselben Regeln wie am Server, eintraege.ts).
//
// Seit E3 (S6, S7) Fragen, Antworten, „erledigt“ und der Montagetermin: Sie
// entstehen aus dem, was das Gerät an der Besichtigung dazu gesagt hat
// (gespraech.ts), stehen in der Reihe, wo sie vorgemerkt wurden (nach dem,
// woran sie hängen), gehören nie zu einem Nachtrag und zählen nicht beim
// Abgleich des Abschlusses.
//
// Seit E3b (O65) das Material von draußen: ein Stück der Besichtigung wie
// eine Länge – es trägt seine Sendung und zählt beim Abgleich mit. Welche
// Tabellen dazugehören, steht an einer Stelle (Kern, ABGLEICH_TABELLEN; am
// Gerät `stueckeTabellen` in speicher.ts).
//
// Wer sendet, steht in sender.ts.

import Dexie, { type Transaction } from "dexie";
import {
  ABGLEICH_TABELLEN,
  istKennung,
  leereKennungsListen,
  STATIONEN,
  type Eintrag,
  type EintragArt,
  type KennungsListen,
  type LoeschbareTabelle,
} from "@happy-energy/kern";
import {
  speicher,
  stueckeTabellen,
  type Foto,
  type K2Teil,
  type Laenge,
  type Materialangabe,
  type Memo,
  type Notiz,
  type PostArt,
  type PostEintrag,
  type Station,
} from "./speicher.ts";
import { dateiAmGeraet } from "./dateien.ts";

// --- Bescheid sagen, wenn etwas dazugekommen ist -------------------------------------

const zuhoerer = new Set<() => void>();

/** Der Sender hört hier zu: Nach jedem `vormerken` versucht er zu senden. */
export function beiNeuemEintrag(melden: () => void): () => void {
  zuhoerer.add(melden);
  return () => zuhoerer.delete(melden);
}

function bescheidSagen(): void {
  for (const z of zuhoerer) z();
}

/**
 * Bescheid sagen, sobald gespeichert ist. Läuft das Vormerken in einer
 * größeren Transaktion (Arbeitskopie und Postausgang in einem Zug), erst
 * nach deren Abschluss – vorher sähe der Sender den Eintrag noch nicht.
 */
function nachDemSpeichern(aussen: Transaction | null): void {
  if (aussen) aussen.on("complete", bescheidSagen);
  else bescheidSagen();
}

/**
 * Die Tabellen, die jede Transaktion mitnehmen muss, in der vorgemerkt
 * wird: Der Postausgang schreibt dabei auch den Fehler an die Aufnahme.
 */
export function postausgangTabellen() {
  return [speicher.postausgang, speicher.aufnahmen] as const;
}

// --- Vormerken und austragen -----------------------------------------------------------

export interface VormerkenZusatz {
  nachtragId?: string;
  /** Pflicht bei „geloescht“. */
  tabelle?: LoeschbareTabelle;
  /** Nur bei „verwerfen“: Name und Adresse für den Hinweis (PostEintrag.verworfen). */
  verworfen?: { name: string; ort: string };
}

/**
 * Merkt eine Sache zum Senden vor. Gibt es für sie schon einen Eintrag,
 * erhöht sich nur `version` – der Platz in der Reihe bleibt. Ein
 * dauerhafter Fehler daran gilt damit als überholt: Der neue Stand wird
 * neu versucht.
 *
 * Der Eintrag trägt die Sendung, mit der er zuletzt vorgemerkt wurde (seit
 * Schritt 5): Was hinausgeht, ist der Stand von jetzt – also gehört es auch
 * zur Sendung von jetzt. Vorkommen kann ein Wechsel kaum (die erste Sendung
 * ist bestätigt, bevor ein Nachtrag beginnt, und je Besichtigung läuft
 * einer) – so trägt aber kein Eintrag still einen veralteten Nachtrag.
 */
export async function vormerken(art: PostArt, kennung: string, aufnahmeId: string, zusatz: VormerkenZusatz = {}): Promise<void> {
  if (art === "geloescht" && !zusatz.tabelle) throw new Error("„geloescht“ braucht die Tabelle.");
  const aussen = Dexie.currentTransaction as Transaction | null;
  await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
    const da = await speicher.postausgang.where("[art+kennung]").equals([art, kennung]).first();
    if (da?.nr !== undefined) {
      await speicher.postausgang
        .where("nr")
        .equals(da.nr)
        .modify((e) => {
          e.version = da.version + 1;
          e.fehler = null;
          e.elternFehlt = 0;
          e.naechsterVersuch = 0;
          if (zusatz.nachtragId) e.nachtragId = zusatz.nachtragId;
          else delete e.nachtragId;
        });
      if (da.fehler !== null) await aufnahmeFehlerAktualisieren(aufnahmeId);
    } else {
      await speicher.postausgang.add(neuerEintrag(art, kennung, aufnahmeId, zusatz, 1));
    }
  });
  nachDemSpeichern(aussen);
}

/** Ein frischer Eintrag – ohne Versuche, ohne Fehler, sofort dran. */
function neuerEintrag(art: PostArt, kennung: string, aufnahmeId: string, zusatz: HintenZusatz, version: number): PostEintrag {
  return {
    art,
    kennung,
    aufnahmeId,
    ...(zusatz.nachtragId ? { nachtragId: zusatz.nachtragId } : {}),
    ...(zusatz.tabelle ? { tabelle: zusatz.tabelle } : {}),
    ...(zusatz.verworfen ? { verworfen: { ...zusatz.verworfen } } : {}),
    ...(zusatz.heilversuche ? { heilversuche: zusatz.heilversuche } : {}),
    ...(zusatz.k2Teile ? { k2Teile: [...zusatz.k2Teile] } : {}),
    version,
    versuche: 0,
    elternFehlt: 0,
    fehler: null,
    naechsterVersuch: 0,
  };
}

export interface HintenZusatz extends VormerkenZusatz {
  /** Nur beim Abschluss: wie oft schon nachgeschickt wurde, was dem Server fehlte. */
  heilversuche?: number;
  /** Nur bei „k2“ (seit E3c, S5): was an der Dachplanung zu senden ist (PostEintrag.k2Teile). */
  k2Teile?: K2Teil[];
}

/**
 * Stellt eine Sache hinten in der Reihe neu an (seit E2, Schritt 4): Ein
 * wartender Eintrag dafür fällt weg, ein frischer kommt ans Ende. Anders
 * als `vormerken`, das den Platz behält – hier soll die Sache NACH allem
 * stehen, was vor ihr hinausgehen muss: der Abschluss nach allem
 * Nachgeschickten, ein Foto nach seiner neu vorgemerkten Datei. Ein
 * dauerhafter Fehler am alten Eintrag ist damit vergessen.
 */
export async function hintenAnstellen(art: PostArt, kennung: string, aufnahmeId: string, zusatz: HintenZusatz = {}): Promise<void> {
  if (art === "geloescht" && !zusatz.tabelle) throw new Error("„geloescht“ braucht die Tabelle.");
  const aussen = Dexie.currentTransaction as Transaction | null;
  await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
    const da = await speicher.postausgang.where("[art+kennung]").equals([art, kennung]).first();
    if (da?.nr !== undefined) await speicher.postausgang.delete(da.nr);
    await speicher.postausgang.add(neuerEintrag(art, kennung, aufnahmeId, zusatz, (da?.version ?? 0) + 1));
    if (da && da.fehler !== null) await aufnahmeFehlerAktualisieren(aufnahmeId);
  });
  nachDemSpeichern(aussen);
}

/**
 * Nimmt einen noch nicht gesendeten Eintrag wieder heraus – etwa die
 * Länge, wenn sie geleert wird (dann geht stattdessen „geloescht“ hinaus).
 * Ist er gerade unterwegs, schadet das nicht: Danach ist nichts mehr zu löschen.
 */
export async function austragen(art: PostArt, kennung: string): Promise<void> {
  await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
    const da = await speicher.postausgang.where("[art+kennung]").equals([art, kennung]).first();
    if (da?.nr === undefined) return;
    await speicher.postausgang.delete(da.nr);
    if (da.fehler !== null) await aufnahmeFehlerAktualisieren(da.aufnahmeId);
  });
}

/**
 * Der Server hat „ok“ gesagt: Eintrag löschen – aber nur, wenn er noch
 * dieselbe Version hat wie beim Senden. Gibt zurück, ob gelöscht wurde.
 * `dabei` (seit E3): was im selben Zug noch an der Besichtigung zu vermerken
 * ist – nur, wenn gelöscht wurde (Fragen und Termin: „der Server hat es“,
 * gespraech.ts). Es darf nur Postausgang und Besichtigungen anfassen.
 */
export async function erledigt(p: PostEintrag, dabei?: () => Promise<void>): Promise<boolean> {
  if (p.nr === undefined) return false;
  const nr = p.nr;
  return speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
    const jetzt = await speicher.postausgang.get(nr);
    if (!jetzt || jetzt.version !== p.version) return false;
    await speicher.postausgang.delete(nr);
    if (jetzt.fehler !== null) await aufnahmeFehlerAktualisieren(jetzt.aufnahmeId);
    if (dabei) await dabei();
    return true;
  });
}

/**
 * Schreibt den ersten dauerhaften Fehler im Postausgang an die Aufnahme –
 * oder nimmt ihn weg, wenn keiner mehr da ist. Die Oberfläche liest ihn dort.
 */
export async function aufnahmeFehlerAktualisieren(aufnahmeId: string): Promise<void> {
  const eintraege = await speicher.postausgang.where("aufnahmeId").equals(aufnahmeId).sortBy("nr");
  const fehler = eintraege.find((e) => e.fehler !== null)?.fehler ?? null;
  const a = await speicher.aufnahmen.get(aufnahmeId);
  if (a && a.fehler !== fehler) await speicher.aufnahmen.update(aufnahmeId, { fehler });
}

/**
 * „Nochmal senden“: alle Einträge der Aufnahme sofort wieder versuchen,
 * Fehler vergessen – auch die Heilversuche des Abschlusses fangen neu an.
 */
export async function erneutVersuchen(aufnahmeId: string): Promise<void> {
  const aussen = Dexie.currentTransaction as Transaction | null;
  await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
    await speicher.postausgang
      .where("aufnahmeId")
      .equals(aufnahmeId)
      .modify((e) => {
        e.fehler = null;
        e.elternFehlt = 0;
        e.naechsterVersuch = 0;
        if (e.heilversuche) e.heilversuche = 0;
      });
    await aufnahmeFehlerAktualisieren(aufnahmeId);
  });
  nachDemSpeichern(aussen);
}

/**
 * Kommt das Netz zurück (oder die App wieder nach vorne), wartet niemand
 * mehr auf seinen Abstand – und der Abstand fängt wieder klein an.
 * Dauerhafte Fehler bleiben, wie sie sind.
 */
export async function abstaendeVergessen(): Promise<void> {
  await speicher.postausgang.filter((e) => e.naechsterVersuch > 0 || e.versuche > 0).modify({ naechsterVersuch: 0, versuche: 0 });
}

// --- Den Inhalt bauen – aus der Arbeitskopie, je Art eine Funktion ------------------------

/** Ergebnis: der Eintrag – oder null, wenn es die Sache so nicht mehr gibt (dann nichts senden). */
type Bauer = (p: PostEintrag) => Promise<Eintrag | null>;

/**
 * Die Sendung des Eintrags (seit Schritt 5): Was im Zuge von „Etwas
 * nachreichen“ vorgemerkt wurde, trägt seinen Nachtrag – an jedem Eintrag
 * außer der Dachplanung mit K2 (die gehört nie zu einem Nachtrag).
 */
function mitNachtrag(p: PostEintrag): { nachtragId?: string } {
  return p.nachtragId ? { nachtragId: p.nachtragId } : {};
}

/** Leer oder nur Leerzeichen gilt als „nicht angegeben“. */
function textOderNull(text: string | null): string | null {
  return text !== null && text.trim() !== "" ? text : null;
}

const BAUER: Record<EintragArt, Bauer> = {
  async aufnahme(p) {
    return speicher.transaction("r", speicher.aufnahmen, speicher.stationen, async () => {
      const a = await speicher.aufnahmen.get(p.kennung);
      if (!a) return null;
      const stationen = await speicher.stationen.where("aufnahmeId").equals(a.id).sortBy("nr");
      return {
        art: "aufnahme",
        aufnahmeId: a.id,
        kennung: a.id,
        kunde: { ...a.kunde },
        objekt: { ...a.objekt },
        anlage: { ...a.anlage },
        begonnenAm: a.begonnenAm,
        stationen: stationen.map((s) => ({ id: s.id, art: s.art })),
      };
    });
  },

  async station(p) {
    const s = await speicher.stationen.get(p.kennung);
    if (!s) return null;
    return {
      art: "station",
      aufnahmeId: s.aufnahmeId,
      kennung: s.id,
      ...mitNachtrag(p),
      fertig: s.fertig,
      sonstiges: textOderNull(s.sonstiges),
      // Seit E3c (S5): Höhe und Zugang am Dach – nur, wenn das Gerät sie kennt (eine Station von vor dem
      // Update kennt sie nicht; dann bleibt am Server, was dort steht). Nichts angegeben: leer.
      ...(s.art === "dach" && s.fachfragen !== undefined ? { fachfragen: s.fachfragen ?? {} } : {}),
    };
  },

  async notiz(p) {
    const n = await speicher.notizen.get(p.kennung);
    if (!n) return null;
    // Leer und ohne Sprachmemo: Diese Notiz gibt es nicht (mehr) – dafür ging „geloescht“ hinaus.
    const memos = await speicher.memos.where("notizId").equals(n.id).count();
    if (n.text.trim() === "" && memos === 0) return null;
    return {
      art: "notiz",
      aufnahmeId: n.aufnahmeId,
      kennung: n.id,
      ...mitNachtrag(p),
      stationId: n.stationId,
      fotoId: n.fotoId,
      text: n.text,
    };
  },

  async laenge(p) {
    const l = await speicher.laengen.get(p.kennung);
    if (!l || l.meter === null) return null;
    return {
      art: "laenge",
      aufnahmeId: l.aufnahmeId,
      kennung: l.id,
      ...mitNachtrag(p),
      stationId: l.stationId,
      laengenArt: l.laengenArt,
      meter: l.meter,
      anmerkung: textOderNull(l.anmerkung),
    };
  },

  // Seit E3b: Material von draußen – wie eine Länge, mit seiner Sendung.
  async material(p) {
    const m = await speicher.materialangaben.get(p.kennung);
    if (!m) return null;
    return {
      art: "material",
      aufnahmeId: m.aufnahmeId,
      kennung: m.id,
      ...mitNachtrag(p),
      stationId: m.stationId,
      standardId: m.standardId,
      artikelnummer: m.artikelnummer,
      bezeichnung: m.bezeichnung,
      menge: m.menge,
      einheit: m.einheit,
    };
  },

  geloescht(p) {
    if (!p.tabelle) return Promise.reject(new Error("Im Postausgang fehlt bei „geloescht“ die Tabelle."));
    return Promise.resolve({ art: "geloescht", aufnahmeId: p.aufnahmeId, kennung: p.kennung, ...mitNachtrag(p), tabelle: p.tabelle });
  },

  // Seit E3c (S6): „Besichtigung verwerfen“ – die Besichtigung ist am Gerät schon weg, der Eintrag trägt nur ihre Kennung.
  verwerfen(p) {
    return Promise.resolve({ art: "verwerfen", aufnahmeId: p.aufnahmeId, kennung: p.kennung });
  },

  async foto(p) {
    const f = await speicher.fotos.get(p.kennung);
    if (!f) return null;
    return {
      art: "foto",
      aufnahmeId: f.aufnahmeId,
      kennung: f.id,
      ...mitNachtrag(p),
      stationId: f.stationId,
      reihenfolge: f.reihenfolge,
      pruefsumme: f.pruefsumme,
      aufnahmezeit: f.aufnahmezeit,
      // Alles wieder weggenommen heißt: keine Zeichnung.
      zeichnung: f.zeichnung && f.zeichnung.striche.length > 0 ? f.zeichnung : null,
    };
  },

  async memo(p) {
    const m = await speicher.memos.get(p.kennung);
    // Ohne Prüfsumme läuft die Aufnahme noch – vorgemerkt wird ein Memo erst, wenn es fertig ist.
    if (!m || m.pruefsumme === null) return null;
    return {
      art: "memo",
      aufnahmeId: m.aufnahmeId,
      kennung: m.id,
      ...mitNachtrag(p),
      notizId: m.notizId,
      pruefsumme: m.pruefsumme,
      format: m.format,
      dauerSekunden: m.dauerSekunden,
      aufgenommenAm: m.aufgenommenAm,
    };
  },

  async k2(p) {
    const k = await speicher.k2.get(p.kennung);
    if (!k) return null;
    // Seit E3c (S5) nur, was sich am Gerät geändert hat (k2Teile; von vor dem Update: das PDF) – das PDF
    // (oder „übersprungen“), der Excel-Report. Was fehlt, bleibt am Server, wie es ist.
    const teile = p.k2Teile ?? ["pdf"];
    const pdf = teile.includes("pdf") && (k.uebersprungen || k.pruefsumme !== null) ? { pruefsumme: k.uebersprungen ? null : k.pruefsumme } : {};
    const excel = teile.includes("excel") && k.excel ? { excel: k.excel.pruefsumme } : {};
    // Nichts davon da: Es gibt nichts zu sagen.
    if (!("pruefsumme" in pdf) && !("excel" in excel)) return null;
    // Ohne Nachtrag – auch nach dem Senden: Die Dachplanung hängt an der Anlage, nie an einer Sendung (Vertrag).
    return { art: "k2", aufnahmeId: k.aufnahmeId, kennung: k.id, ...pdf, ...excel };
  },

  async abgeschlossen(p) {
    const kennungen = await kennungenSammeln(p.kennung);
    if (!kennungen) return null;
    return { art: "abgeschlossen", aufnahmeId: p.kennung, kennung: p.kennung, kennungen };
  },

  // „Etwas nachreichen“ beginnt (seit Schritt 5) – die Kennung ist die des Nachtrags, er trägt sich selbst.
  async nachtrag(p) {
    const n = await speicher.nachtraege.get(p.kennung);
    if (!n) return null;
    return { art: "nachtrag", aufnahmeId: n.aufnahmeId, kennung: n.id, nachtragId: n.id, begonnenAm: n.begonnenAm };
  },

  async "nachtrag-abgeschlossen"(p) {
    const gesammelt = await nachtragKennungenSammeln(p.kennung);
    if (!gesammelt) return null;
    const { aufnahmeId, kennungen } = gesammelt;
    return { art: "nachtrag-abgeschlossen", aufnahmeId, kennung: p.kennung, nachtragId: p.kennung, kennungen };
  },

  // Seit E3 (S6, S7): Fragen, Antworten, „erledigt“ und der Montagetermin – aus dem, was das Gerät
  // an der Besichtigung dazu gesagt hat (gespraech.ts). Jederzeit erlaubt, nie Teil eines Nachtrags:
  // ohne `nachtragId`. Steht es dort nicht mehr, hat die Kopie vom Server es schon (aufgeräumt) – nichts zu senden.
  async frage(p) {
    const a = await speicher.aufnahmen.get(p.aufnahmeId);
    const f = a?.eigeneFragen?.find((x) => x.id === p.kennung);
    if (!a || !f) return null;
    return { art: "frage", aufnahmeId: a.id, kennung: f.id, bezug: { ...f.bezug }, text: f.text, gestelltAm: f.gestelltAm };
  },

  async antwort(p) {
    const a = await speicher.aufnahmen.get(p.aufnahmeId);
    const x = a?.eigeneAntworten?.find((y) => y.id === p.kennung);
    if (!a || !x) return null;
    return { art: "antwort", aufnahmeId: a.id, kennung: x.id, frageId: x.frageId, text: x.text, geantwortetAm: x.geantwortetAm };
  },

  // Die Kennung ist die der Frage.
  async "frage-stand"(p) {
    const a = await speicher.aufnahmen.get(p.aufnahmeId);
    const s = a?.eigeneStaende?.find((y) => y.frageId === p.kennung);
    if (!a || !s) return null;
    return { art: "frage-stand", aufnahmeId: a.id, kennung: s.frageId, stand: s.stand };
  },

  // Die Kennung ist die der Anlage – wie bei der Dachplanung mit K2.
  async montagetermin(p) {
    const a = await speicher.aufnahmen.get(p.aufnahmeId);
    const t = a?.eigenerTermin;
    if (!a || !t || a.anlage.id !== p.kennung) return null;
    return { art: "montagetermin", aufnahmeId: a.id, kennung: a.anlage.id, am: t.am, text: t.text };
  },
};

/**
 * Baut den Eintrag, wie er jetzt an den Server ginge – noch ungeprüft; der
 * Sender prüft ihn vor dem Senden mit `eintragEingabe`.
 */
export function eintragBauen(p: PostEintrag): Promise<Eintrag | null> {
  if (p.art === "datei") return Promise.reject(new Error("Eine Datei ist kein Eintrag – sie geht über den Datei-Eingang."));
  return BAUER[p.art](p);
}

// --- Der Abschluss (seit E2, Schritt 4) ------------------------------------------------------

/**
 * Was nach allen anderen Einträgen am Server stehen muss – die Kennungen
 * einer Besichtigung je Tabelle, für den Abgleich „nichts fehlt“. Nur
 * Stücke der Besichtigung selbst, keine aus „Etwas nachreichen“ (die
 * tragen einen Nachtrag): alle Stationen, alle Fotos, die Notizen, die es
 * „gibt“ (Text oder ein fertiges Sprachmemo – dieselbe Regel wie beim
 * Bauen der Notiz), die fertigen Memos, die Längen mit Wert und (seit E3b)
 * das Material. Null, wenn es die Besichtigung am Gerät nicht gibt.
 */
export async function kennungenSammeln(aufnahmeId: string): Promise<KennungsListen | null> {
  return speicher.transaction("r", [speicher.aufnahmen, ...stueckeTabellen()], async () => {
    if (!(await speicher.aufnahmen.get(aufnahmeId))) return null;
    const { stationen, fotos, notizen, memos, laengen, materialangaben } = await stueckeLesen(aufnahmeId);
    // Ein Memo ohne Prüfsumme wird gerade aufgenommen – es ist noch nicht da.
    const fertigeMemos = memos.filter((m) => m.pruefsumme !== null);
    const mitMemo = new Set(fertigeMemos.map((m) => m.notizId));
    const ohneNachtrag = (x: { nachtragId: string | null }) => x.nachtragId === null;
    return {
      station: stationen.map((s) => s.id),
      foto: fotos.filter(ohneNachtrag).map((f) => f.id),
      notiz: notizen.filter((n) => ohneNachtrag(n) && (n.text.trim() !== "" || mitMemo.has(n.id))).map((n) => n.id),
      sprachmemo: fertigeMemos.filter(ohneNachtrag).map((m) => m.id),
      laenge: laengen.filter((l) => ohneNachtrag(l) && l.meter !== null).map((l) => l.id),
      materialangabe: materialangaben.filter(ohneNachtrag).map((m) => m.id),
    };
  });
}

// --- Was zu einem Nachtrag gehört (seit E2, Schritt 5) ----------------------------------------

/** Die Stücke einer Besichtigung am Gerät – so, wie Arbeitskopie und Oberfläche sie lesen. */
export interface BesichtigungsStuecke {
  stationen: readonly Station[];
  fotos: readonly Foto[];
  notizen: readonly Notiz[];
  memos: readonly Memo[];
  laengen: readonly Laenge[];
  /** Seit E3b: das Material von draußen. */
  materialangaben: readonly Materialangabe[];
}

/** Was ein Nachtrag bisher gebracht hat – je Tabelle die Stücke. */
export interface NachtragStuecke {
  /** Stationen, deren „Sonstiges“ er berichtigt hat. */
  stationen: Station[];
  fotos: Foto[];
  notizen: Notiz[];
  memos: Memo[];
  /** Neue Längen (mit Wert) und berichtigte. */
  laengen: Laenge[];
  /** Neues Material und berichtigtes (seit E3b). */
  materialangaben: Materialangabe[];
}

/**
 * Was zu diesem Nachtrag gehört – genau nach den Regeln, nach denen der
 * Server ihn beim Senden abgleicht (eintraege.ts, kennungenDerSendung):
 * Stationen, die er berichtigt hat; Fotos, Notizen und Memos, die mit ihm
 * dazukamen – Notizen nur, wenn es sie „gibt“ (Text oder ein fertiges
 * Sprachmemo), Memos nur fertige; Längen, die mit ihm dazukamen (mit Wert),
 * und die, die er berichtigt hat; ebenso das Material. Eine Regel für den Abschluss des
 * Nachtrags, für „ist er leer?“ und für das, was die Oberfläche als neu
 * zeigt.
 */
export function nachtragStuecke(b: BesichtigungsStuecke, nachtragId: string): NachtragStuecke {
  const vonIhm = (x: { nachtragId: string | null }) => x.nachtragId === nachtragId;
  const memos = b.memos.filter((m) => vonIhm(m) && m.pruefsumme !== null);
  const mitMemo = new Set(memos.map((m) => m.notizId));
  return {
    stationen: b.stationen.filter((s) => s.geaendertImNachtrag === nachtragId),
    fotos: b.fotos.filter(vonIhm),
    notizen: b.notizen.filter((n) => vonIhm(n) && (n.text.trim() !== "" || mitMemo.has(n.id))),
    memos,
    laengen: b.laengen.filter((l) => (vonIhm(l) && l.meter !== null) || l.geaendertImNachtrag === nachtragId),
    materialangaben: b.materialangaben.filter((m) => vonIhm(m) || m.geaendertImNachtrag === nachtragId),
  };
}

/**
 * Ist im Nachtrag noch nichts – nichts dazugekommen, nichts berichtigt und
 * kein Sprachmemo, das gerade für ihn aufgenommen wird? Dann lässt er sich
 * abbrechen statt senden.
 */
export function nachtragLeer(b: BesichtigungsStuecke, nachtragId: string): boolean {
  const s = nachtragStuecke(b, nachtragId);
  const laeuft = b.memos.some((m) => m.nachtragId === nachtragId && m.pruefsumme === null);
  const stuecke = s.stationen.length + s.fotos.length + s.notizen.length + s.memos.length + s.laengen.length + s.materialangaben.length;
  return !laeuft && stuecke === 0;
}

/** Die Stücke einer Besichtigung aus dem Speicher – in einer Transaktion mit `stueckeTabellen()` (speicher.ts). */
export async function stueckeLesen(aufnahmeId: string): Promise<BesichtigungsStuecke> {
  const [stationen, fotos, notizen, memos, laengen, materialangaben] = await Promise.all([
    speicher.stationen.where("aufnahmeId").equals(aufnahmeId).sortBy("nr"),
    speicher.fotos.where("aufnahmeId").equals(aufnahmeId).toArray(),
    speicher.notizen.where("aufnahmeId").equals(aufnahmeId).toArray(),
    speicher.memos.where("aufnahmeId").equals(aufnahmeId).toArray(),
    speicher.laengen.where("aufnahmeId").equals(aufnahmeId).toArray(),
    speicher.materialangaben.where("aufnahmeId").equals(aufnahmeId).toArray(),
  ]);
  return { stationen, fotos, notizen, memos, laengen, materialangaben };
}

/**
 * Die Kennungen eines Nachtrags für „nachtrag-abgeschlossen“ – je Tabelle,
 * nach `nachtragStuecke`. Null, wenn es den Nachtrag am Gerät nicht gibt.
 * Ein leerer Nachtrag hat leere Listen – auch so schließt ihn der Server.
 */
export async function nachtragKennungenSammeln(nachtragId: string): Promise<{ aufnahmeId: string; kennungen: KennungsListen } | null> {
  return speicher.transaction("r", [speicher.nachtraege, ...stueckeTabellen()], async () => {
    const n = await speicher.nachtraege.get(nachtragId);
    if (!n) return null;
    const s = nachtragStuecke(await stueckeLesen(n.aufnahmeId), nachtragId);
    const ids = (liste: { id: string }[]) => liste.map((x) => x.id);
    return {
      aufnahmeId: n.aufnahmeId,
      kennungen: {
        station: ids(s.stationen),
        foto: ids(s.fotos),
        notiz: ids(s.notizen),
        sprachmemo: ids(s.memos),
        laenge: ids(s.laengen),
        materialangabe: ids(s.materialangaben),
      },
    };
  });
}

/**
 * Die Kennungslisten aus einer Antwort des Servers („fehlt“, „zuViel“) –
 * jede Tabelle als Liste, was keine Kennung ist, fällt weg. Ohne Angabe:
 * leere Listen.
 */
export function kennungsListenLesen(roh: unknown): KennungsListen {
  const quelle = roh !== null && typeof roh === "object" ? (roh as Record<string, unknown>) : {};
  const listen: KennungsListen = leereKennungsListen();
  for (const t of ABGLEICH_TABELLEN) {
    const liste = quelle[t];
    if (Array.isArray(liste)) listen[t] = liste.filter(istKennung);
  }
  return listen;
}

/** Wie viele Kennungen insgesamt in den Listen stehen – für die Fehlerwache, nie die Kennungen selbst. */
export function kennungenZaehlen(listen: KennungsListen): number {
  return ABGLEICH_TABELLEN.reduce((summe, t) => summe + listen[t].length, 0);
}

/**
 * Der Server sagt beim Abschluss „unvollstaendig“: Das heilt die App
 * selbst. Was am Server fehlt, merkt sie neu vor (Fotos und Memos mit ihrer
 * Datei davor, wenn sie am Gerät liegt); was dort zu viel ist, löscht sie
 * („geloescht“). Alles in der Reihenfolge, in der es aneinander hängt, und
 * jedes hinten angestellt – danach der Abschluss selbst, wieder hinten, mit
 * einem Heilversuch mehr. Eine Station „zu viel“ ist so nicht zu heilen;
 * das entscheidet der Sender vorher.
 *
 * Gilt genauso für „nachtrag-abgeschlossen“ (E2, Schritt 5): Was dabei
 * nachgeschickt wird, trägt dann den Nachtrag des Abschlusses.
 */
export async function abschlussHeilen(p: PostEintrag, fehlt: KennungsListen, zuViel: KennungsListen): Promise<void> {
  const a = p.aufnahmeId;
  const nachtrag: VormerkenZusatz = p.nachtragId ? { nachtragId: p.nachtragId } : {};
  // Zu viel am Server: erst, was an etwas hängt (Memo an Notiz, Notiz an Foto), dann Längen und Fotos.
  for (const id of zuViel.sprachmemo) await hintenAnstellen("geloescht", id, a, { ...nachtrag, tabelle: "sprachmemo" });
  for (const id of zuViel.notiz) await hintenAnstellen("geloescht", id, a, { ...nachtrag, tabelle: "notiz" });
  for (const id of zuViel.laenge) await hintenAnstellen("geloescht", id, a, { ...nachtrag, tabelle: "laenge" });
  for (const id of zuViel.materialangabe) await hintenAnstellen("geloescht", id, a, { ...nachtrag, tabelle: "materialangabe" });
  for (const id of zuViel.foto) await hintenAnstellen("geloescht", id, a, { ...nachtrag, tabelle: "foto" });
  // Fehlt am Server: Station, Foto (Datei davor), Notiz (hängt am Foto), Memo (Datei davor, hängt an der Notiz), Länge.
  for (const id of fehlt.station) await hintenAnstellen("station", id, a, nachtrag);
  for (const id of fehlt.foto) {
    const f = await speicher.fotos.get(id);
    if (f) await dateiNeuAnstellen(f.pruefsumme, a);
    await hintenAnstellen("foto", id, a, nachtrag);
  }
  for (const id of fehlt.notiz) await hintenAnstellen("notiz", id, a, nachtrag);
  for (const id of fehlt.sprachmemo) {
    const m = await speicher.memos.get(id);
    if (m?.pruefsumme) await dateiNeuAnstellen(m.pruefsumme, a);
    await hintenAnstellen("memo", id, a, nachtrag);
  }
  for (const id of fehlt.laenge) await hintenAnstellen("laenge", id, a, nachtrag);
  // Material hängt höchstens an einer Station – die gibt es mit der Aufnahme.
  for (const id of fehlt.materialangabe) await hintenAnstellen("material", id, a, nachtrag);
  await hintenAnstellen(p.art, p.kennung, a, { ...nachtrag, heilversuche: (p.heilversuche ?? 0) + 1 });
}

/**
 * Die Datei eines nachgeschickten Fotos oder Memos noch einmal hochladen –
 * wenn sie am Gerät liegt. Sonst nicht: Hat der Server sie doch nicht, sagt
 * er beim Foto „datei-fehlt“, und das zeigt die Besichtigung als Fehler.
 */
async function dateiNeuAnstellen(pruefsumme: string, aufnahmeId: string): Promise<void> {
  if (!(await dateiFinden(pruefsumme))) return;
  await dateiIstAmServer(pruefsumme, false);
  await hintenAnstellen("datei", pruefsumme, aufnahmeId);
}

// --- Dateien ------------------------------------------------------------------------------

/**
 * Die Datei mit dieser Prüfsumme, solange sie am Gerät liegt – null, wenn
 * nicht (mehr): Dann gibt es nichts hochzuladen. Ein Foto oder ein Memo
 * (seit 19.10.2026 aus „dateien“, dateien.ts) oder das K2-PDF – beide liegen
 * als ArrayBuffer am Gerät und gehen als frischer Blob mit `type` hinaus.
 * Nie vom Server: Der Sender lädt nur hoch, was hier liegt.
 */
export function dateiFinden(pruefsumme: string): Promise<Blob | null> {
  return dateiAmGeraet(pruefsumme);
}

/** Der Server hat die Datei: an jedem Foto, Memo, K2-PDF und (seit E3c, S5) Excel-Report mit dieser Prüfsumme vermerken. */
export async function dateiIstAmServer(pruefsumme: string, amServer = true): Promise<void> {
  await speicher.transaction("rw", speicher.fotos, speicher.memos, speicher.k2, async () => {
    await speicher.fotos.where("pruefsumme").equals(pruefsumme).modify({ hochgeladen: amServer });
    await speicher.memos.filter((x) => x.pruefsumme === pruefsumme).modify({ hochgeladen: amServer });
    await speicher.k2.filter((x) => x.pruefsumme === pruefsumme).modify({ hochgeladen: amServer });
    await speicher.k2
      .filter((x) => x.excel?.pruefsumme === pruefsumme)
      .modify((x) => {
        if (x.excel) x.excel.hochgeladen = amServer;
      });
  });
}

// --- Für die Oberfläche: Was wartet noch? ------------------------------------------------

export interface PostausgangStand {
  /** Alle Einträge, die noch nicht beim Server sind. */
  wartend: number;
  /** Je Aufnahme: wie viele noch warten. */
  jeAufnahme: Record<string, number>;
}

/**
 * Was wartet. Ein Foto zählt einmal, nicht zweimal (Datei und Eintrag) –
 * eine Datei zählt nur, wenn sonst nichts mehr wartet. So steht nie „alles
 * gesendet“, solange noch etwas am Gerät liegt.
 */
export async function postausgangStand(): Promise<PostausgangStand> {
  const alle = await speicher.postausgang.toArray();
  const zaehlen = (liste: PostEintrag[]) => liste.filter((e) => e.art !== "datei").length || liste.length;
  const jeAufnahmeListe: Record<string, PostEintrag[]> = {};
  for (const e of alle) (jeAufnahmeListe[e.aufnahmeId] ??= []).push(e);
  const jeAufnahme: Record<string, number> = {};
  for (const [id, liste] of Object.entries(jeAufnahmeListe)) jeAufnahme[id] = zaehlen(liste);
  return { wartend: zaehlen(alle), jeAufnahme };
}

/** Wie viele der sieben Stationen fertig sind – für Liste und Fortschritt. */
export function stationenFertig(stationen: readonly { fertig: boolean }[]): number {
  return stationen.filter((s) => s.fertig).length;
}

export const STATIONEN_ZAHL = STATIONEN.length;
